import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  viewerMutationOptions,
  viewerQueryKeys,
  type AreaTag,
  type LayoutPoint,
} from '@ironview/interactive-viewer';

import {
  createIronview360ViewerContext,
  ironview360ViewerAdapter,
} from '../adapters/ironview360DataAdapter';

export interface CreateViewerAreaTagData {
  name: string;
  points: readonly LayoutPoint[];
  markerIds?: readonly (string | number)[];
  photoIds?: readonly (string | number)[];
}

export interface UpdateViewerAreaTagData {
  name?: string;
  coordinates?: readonly LayoutPoint[];
  markerIds?: readonly (string | number)[];
  photoIds?: readonly (string | number)[];
  textPosition?: LayoutPoint | null;
  textSize?: number | null;
  textRotation?: number | null;
}

const areaTagQueryKey = (projectId: string, layoutId: string) =>
  viewerQueryKeys.areaTags(
    ironview360ViewerAdapter.id,
    projectId,
    layoutId,
  );

const requireScope = (layoutId: string, projectId: string) => {
  if (!layoutId) throw new Error('Layout ID is required for units/locations');
  if (!projectId) throw new Error('Project ID is required for units/locations');
  return createIronview360ViewerContext(projectId);
};

export const useCreateViewerAreaTag = (
  layoutId: string,
  projectId: string,
) => {
  const queryClient = useQueryClient();
  const queryKey = areaTagQueryKey(projectId, layoutId);

  return useMutation<
    AreaTag,
    Error,
    CreateViewerAreaTagData,
    {
      previous?: readonly AreaTag[];
      optimisticId: number;
    }
  >({
    mutationFn: (data) =>
      viewerMutationOptions
        .createAreaTag(ironview360ViewerAdapter)
        .mutationFn({
          context: requireScope(layoutId, projectId),
          layoutId,
          name: data.name,
          points: data.points,
          markerIds: data.markerIds ?? [],
          photoIds: data.photoIds ?? [],
        }),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<readonly AreaTag[]>(queryKey);
      const optimisticId = -Date.now();
      const optimisticAreaTag: AreaTag = {
        id: optimisticId,
        name: data.name,
        points: data.points,
        markerIds: data.markerIds ?? [],
        photoIds: data.photoIds ?? [],
        metadata: { sourceLayoutId: layoutId },
      };
      queryClient.setQueryData<readonly AreaTag[]>(
        queryKey,
        (current) => [...(current ?? []), optimisticAreaTag],
      );
      return { previous, optimisticId };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSuccess: (created, _variables, context) => {
      queryClient.setQueryData<readonly AreaTag[]>(
        queryKey,
        (current) =>
          current?.map((areaTag) =>
            areaTag.id === context.optimisticId ? created : areaTag,
          ),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdateViewerAreaTag = (
  layoutId: string,
  projectId: string,
) => {
  const queryClient = useQueryClient();
  const queryKey = areaTagQueryKey(projectId, layoutId);

  return useMutation<
    AreaTag,
    Error,
    { areaTagId: number; data: UpdateViewerAreaTagData },
    { previous?: readonly AreaTag[] }
  >({
    mutationFn: ({ areaTagId, data }) =>
      viewerMutationOptions
        .updateAreaTag(ironview360ViewerAdapter)
        .mutationFn({
          context: requireScope(layoutId, projectId),
          layoutId,
          areaTagId,
          changes: {
            name: data.name,
            points: data.coordinates,
            markerIds: data.markerIds,
            photoIds: data.photoIds,
            label:
              data.textPosition !== undefined ||
              data.textSize !== undefined ||
              data.textRotation !== undefined
                ? {
                    position: data.textPosition,
                    size: data.textSize,
                    rotation: data.textRotation,
                  }
                : undefined,
          },
        }),
    onMutate: async ({ areaTagId, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<readonly AreaTag[]>(queryKey);
      queryClient.setQueryData<readonly AreaTag[]>(
        queryKey,
        (current) =>
          current?.map((areaTag) => {
            if (Number(areaTag.id) !== areaTagId) return areaTag;
            const nextLabel =
              data.textPosition !== undefined ||
              data.textSize !== undefined ||
              data.textRotation !== undefined
                ? {
                    ...areaTag.label,
                    ...(data.textPosition !== undefined
                      ? { position: data.textPosition }
                      : {}),
                    ...(data.textSize !== undefined
                      ? { size: data.textSize }
                      : {}),
                    ...(data.textRotation !== undefined
                      ? { rotation: data.textRotation }
                      : {}),
                  }
                : areaTag.label;
            return {
              ...areaTag,
              ...(data.name !== undefined ? { name: data.name } : {}),
              ...(data.coordinates !== undefined
                ? { points: data.coordinates }
                : {}),
              ...(data.markerIds !== undefined
                ? { markerIds: data.markerIds }
                : {}),
              ...(data.photoIds !== undefined
                ? { photoIds: data.photoIds }
                : {}),
              label: nextLabel,
            };
          }),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<readonly AreaTag[]>(
        queryKey,
        (current) =>
          current?.map((areaTag) =>
            Number(areaTag.id) === Number(updated.id)
              ? updated
              : areaTag,
          ),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useDeleteViewerAreaTag = (
  layoutId: string,
  projectId: string,
) => {
  const queryClient = useQueryClient();
  const queryKey = areaTagQueryKey(projectId, layoutId);

  return useMutation<
    void,
    Error,
    number,
    { previous?: readonly AreaTag[] }
  >({
    mutationFn: (areaTagId) =>
      viewerMutationOptions
        .deleteAreaTag(ironview360ViewerAdapter)
        .mutationFn({
          context: requireScope(layoutId, projectId),
          layoutId,
          areaTagId,
        }),
    onMutate: async (areaTagId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<readonly AreaTag[]>(queryKey);
      queryClient.setQueryData<readonly AreaTag[]>(
        queryKey,
        (current) =>
          current?.filter(
            (areaTag) => Number(areaTag.id) !== areaTagId,
          ),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
};
